import React from "react";
import toast from "react-hot-toast";
import Select, { StylesConfig } from "react-select";

import businessInfoService, {
  BusinessInfo,
} from "@/services/businessInfoService";
// ...existing code...

const getSelectStyles = (isDark: boolean): StylesConfig<any, false> => ({
  control: (provided) => ({
    ...provided,
    backgroundColor: isDark ? "#1a202c" : "#f9fafb",
    borderColor: isDark ? "#374151" : "#d1d5db",
    boxShadow: "none",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  input: (provided) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDark ? "#1a202c" : "#fff",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? isDark
        ? "#374151"
        : "#e5e7eb"
      : isDark
        ? "#1a202c"
        : "#fff",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
});
import courierService from "@/services/CourierService";

interface AddDeliveryModalProps {
  drivers: Driver[];
  isWarehouse?: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  open: boolean;
  user?: {
    address: string;
    name: string;
    phone: string;
  };
}

interface DeliveryForm {
  deliveryDate: string;
  deliverySpeed: string;
  notes: string;
  parcelType: string;
  pickupDate: string;
  receiverAddress: string;
  receiverName: string;
  receiverPhone: string;
  senderAddress: string;
  senderName: string;
  senderPhone: string;
  subzone: string;
  weight: number;
  zone: string;
}

interface Driver {
  id: string;
  name: string;
}

const parcelTypes = ["Document", "Small Package", "Large Package", "Fragile"];
const deliverySpeeds = ["Standard", "Express", "Same-Day"];

const AddDeliveryModal: React.FC<AddDeliveryModalProps> = ({
  drivers,
  onClose,
  onSubmit,
  open,
  user,
}) => {
  const [source, setSource] = React.useState<"Customer" | "Warehouse">(
    "Customer",
  );
  const [loading, setLoading] = React.useState(false);
  const isWarehouse = source === "Warehouse";
  // Helper to get today's date in YYYY-MM-DD
  const getToday = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Helper to add business days
  const addBusinessDays = (start: Date, minDays: number, maxDays: number) => {
    let daysAdded = 0;
    const date = new Date(start);
    while (daysAdded < minDays) {
      date.setDate(date.getDate() + 1);
      // Skip weekends
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        daysAdded++;
      }
    }
    // For maxDays, pick random between min and max
    if (maxDays > minDays) {
      const extra = Math.floor(Math.random() * (maxDays - minDays + 1));
      let extraAdded = 0;
      while (extraAdded < extra) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) {
          extraAdded++;
        }
      }
    }
    return date.toISOString().split("T")[0];
  };

  // Set default dates
  const [form, setForm] = React.useState<DeliveryForm>(() => {
    const today = getToday();
    // Default: Standard (3-7 business days)
    const deliverySpeed = deliverySpeeds[0];
    const deliveryDate = addBusinessDays(new Date(today), 3, 7);
    return {
      deliveryDate,
      deliverySpeed,
      notes: "",
      parcelType: parcelTypes[0],
      pickupDate: today,
      receiverAddress: "",
      receiverName: "",
      receiverPhone: "",
      senderAddress: "",
      senderName: "",
      senderPhone: "",
      subzone: "",
      weight: 0,
      zone: "",
    };
  });

  const [businessInfo, setBusinessInfo] = React.useState<BusinessInfo | null>(
    null,
  );
  const [zones, setZones] = React.useState<any[]>([]);
  const [subzones, setSubzones] = React.useState<any[]>([]);
  // react-select options
  const zoneOptions = zones.map((zone) => ({
    label: zone.name || zone.zone_name || String(zone.id),
    value: zone.id,
  }));
  const subzoneOptions = subzones.map((subzone) => ({
    label: subzone.name || subzone.subzone_name || String(subzone.id),
    value: subzone.id,
  }));

  // react-select handlers
  const handleZoneSelect = (selected: null | { value: string }) => {
    setForm((f) => ({ ...f, zone: selected ? selected.value : "" }));
  };
  const handleSubzoneSelect = (selected: null | { value: string }) => {
    setForm((f) => ({ ...f, subzone: selected ? selected.value : "" }));
  };

  // Detect dark mode (Tailwind dark class on html or body)
  const [isDark, setIsDark] = React.useState(false);
  React.useEffect(() => {
    const checkDark = () => {
      return (
        document.documentElement.classList.contains("dark") ||
        document.body.classList.contains("dark")
      );
    };
    setIsDark(checkDark());
    const observer = new MutationObserver(() => setIsDark(checkDark()));
    observer.observe(document.documentElement, { attributes: true });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (open && source === "Warehouse") {
      businessInfoService.getBusinessInfo().then((info) => {
        setBusinessInfo(info);
        setForm((f) => ({
          ...f,
          senderAddress: info.address || "",
          senderName:
            info.company_name || info.business_type || info.description || "",
          senderPhone: info.phone || "",
        }));
      });
    }
  }, [open, source]);

  React.useEffect(() => {
    if (source === "Customer") {
      setForm((f) => ({
        ...f,
        senderAddress: "",
        senderName: "",
        senderPhone: "",
      }));
    }
  }, [source]);

  React.useEffect(() => {
    if (open) {
      courierService.getServiceZones().then((data) => {
        setZones(data);
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (form.zone) {
      courierService.getServiceSubzones(form.zone).then((allSubzones) => {
        const filtered = allSubzones.filter(
          (subzone: any) => String(subzone.zone_id) === String(form.zone),
        );
        setSubzones(filtered);
      });
    } else {
      setSubzones([]);
    }
  }, [form.zone]);

  if (!open) return null;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, value } = e.target;
    // If changing deliverySpeed, recalculate deliveryDate
    if (name === "deliverySpeed") {
      const today = getToday();
      let maxDays = 7,
        minDays = 3;
      if (value === "Express") {
        minDays = 1;
        maxDays = 3;
      } else if (value === "Same-Day") {
        minDays = 1;
        maxDays = 1;
      }
      const newDeliveryDate = addBusinessDays(
        new Date(form.pickupDate || today),
        minDays,
        maxDays,
      );
      setForm((f) => ({
        ...f,
        deliveryDate: newDeliveryDate,
        deliverySpeed: value,
      }));
    } else if (name === "pickupDate") {
      // Changing pickupDate, recalc deliveryDate
      const today = getToday();
      let maxDays = 7,
        minDays = 3;
      if (form.deliverySpeed === "Express") {
        minDays = 1;
        maxDays = 3;
      } else if (form.deliverySpeed === "Same-Day") {
        minDays = 1;
        maxDays = 1;
      }
      const newDeliveryDate = addBusinessDays(
        new Date(value),
        minDays,
        maxDays,
      );
      setForm((f) => ({
        ...f,
        deliveryDate: newDeliveryDate,
        pickupDate: value,
      }));
    } else {
      setForm((f) => ({
        ...f,
        [name]: type === "number" ? Number(value) : value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    // validation
    if (!form.senderName || !form.receiverName) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!form.senderAddress || typeof form.senderAddress !== "string") {
      toast.error("Sender identity (ID/passport) is required.");
      return;
    }
    if (!form.receiverAddress || typeof form.receiverAddress !== "string") {
      toast.error("Receiver identity (ID/passport) is required.");
      return;
    }
    const payload = {
      deliveryDate: form.deliveryDate,
      deliverySpeed: form.deliverySpeed,
      notes: form.notes,
      parcelType: form.parcelType,
      pickupDate: form.pickupDate,
      receiverIdPassport: form.receiverAddress,
      receiverName: form.receiverName,
      receiverPhone: form.receiverPhone,
      senderIdPassport: form.senderAddress,
      senderName: form.senderName,
      senderPhone: form.senderPhone,
      subzone: form.subzone,
      weight: form.weight,
      zone: form.zone,
    };
    setLoading(true);
    Promise.resolve(onSubmit(payload))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-gray-100">
          Add Delivery
        </h2>

        <div className="space-y-4">
          {/* Source Selection */}
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Source
            </label>
            <select
              className="w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              onChange={(e) =>
                setSource(e.target.value as "Customer" | "Warehouse")
              }
              value={source}
            >
              <option value="Customer">Customer</option>
              <option value="Warehouse">Warehouse</option>
            </select>
          </div>
          {/* Sender Info */}
          {!isWarehouse ? (
            <>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                Sender Info
              </h3>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
                  name="senderName"
                  onChange={handleChange}
                  required
                  value={form.senderName}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Name
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
                  name="senderPhone"
                  onChange={handleChange}
                  value={form.senderPhone}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Phone
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
                  name="senderAddress"
                  onChange={handleChange}
                  value={form.senderAddress}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Address
                </label>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                Sender Info (Warehouse)
              </h3>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-100 px-2 py-3 text-gray-500 focus:outline-none dark:bg-gray-800 dark:text-gray-400"
                  disabled
                  name="senderName"
                  value={form.senderName}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Name
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-100 px-2 py-3 text-gray-500 focus:outline-none dark:bg-gray-800 dark:text-gray-400"
                  disabled
                  name="senderPhone"
                  value={form.senderPhone}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Phone
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-100 px-2 py-3 text-gray-500 focus:outline-none dark:bg-gray-800 dark:text-gray-400"
                  disabled
                  name="senderAddress"
                  value={form.senderAddress}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender ID/Passport Number
                </label>
              </div>
            </>
          )}

          {/* Receiver Info */}
          <h3 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">
            Receiver Info
          </h3>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="receiverName"
              onChange={handleChange}
              required
              value={form.receiverName}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Receiver Name
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="receiverPhone"
              onChange={handleChange}
              value={form.receiverPhone}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Receiver Phone
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="receiverAddress"
              onChange={handleChange}
              value={form.receiverAddress}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Receiver ID?Passport Number
            </label>
          </div>

          {/* Parcel Details */}
          <h3 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">
            Parcel Details
          </h3>
          <div className="relative mb-4">
            <select
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="parcelType"
              onChange={handleChange}
              value={form.parcelType}
            >
              {parcelTypes.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Parcel Type
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="weight"
              onChange={handleChange}
              type="number"
              value={form.weight}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Weight (kg)
            </label>
          </div>

          {/* Delivery Options */}
          <h3 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">
            Delivery Options
          </h3>
          <div className="relative mb-4">
            <select
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="deliverySpeed"
              onChange={handleChange}
              value={form.deliverySpeed}
            >
              {deliverySpeeds.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Delivery Speed
            </label>
          </div>
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Zone
            </label>
            <Select
              classNamePrefix="react-select"
              isClearable
              onChange={handleZoneSelect}
              options={zoneOptions}
              placeholder="Search or select zone"
              styles={getSelectStyles(isDark)}
              value={zoneOptions.find((z) => z.value === form.zone) || null}
            />
          </div>
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Subzone
            </label>
            <Select
              classNamePrefix="react-select"
              isClearable
              onChange={handleSubzoneSelect}
              options={subzoneOptions}
              placeholder="Search or select subzone"
              styles={getSelectStyles(isDark)}
              value={
                subzoneOptions.find((s) => s.value === form.subzone) || null
              }
            />
          </div>

          {/* Pickup & Delivery Dates */}
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              min={getToday()}
              name="pickupDate"
              onChange={handleChange}
              type="date"
              value={form.pickupDate}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Pickup Date
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              min={form.pickupDate || getToday()}
              name="deliveryDate"
              onChange={handleChange}
              type="date"
              value={form.deliveryDate}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Delivery Date
            </label>
          </div>

          {/* Additional Notes */}
          <div className="relative mb-4">
            <textarea
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="notes"
              onChange={handleChange}
              rows={3}
              value={form.notes}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Additional Notes
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            className="rounded bg-gray-300 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            disabled={loading}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500 ${loading ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={loading}
            type="submit"
          >
            {loading && (
              <svg
                className="h-5 w-5 animate-spin text-white"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  d="M4 12a8 8 0 018-8v8z"
                  fill="currentColor"
                ></path>
              </svg>
            )}
            Add Delivery
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDeliveryModal;
